// =============================================================================
// effect-pipeline.ts — pure-function port of Cards/EffectPipeline.cs
//
// Single funnel for every card-effect application:
//   1. beforeApplyHook
//   2. onInterceptHook (Counterattack / Mitigate veto window)
//   3. predicate gate (Conditional cards via PredicateEval)
//   4. BuildCostMod-on-Whenever guard (deferred-to-build path)
//   5. resolver dispatch via EffectKindResolverRegistry
//   6. afterApplyHook
//   7. (WEB.4.E) trigger dispatch — fire matching passive triggers, depth-gated
//
// Pure-function discipline: all state threaded as explicit arguments.
// No singletons. No module-level state.
// =============================================================================

import type { CardDef, CardEffect, TriggerEvent } from "@gdg/shared";
import type { EffectContext } from "../card-effect-context.js";
import { EffectResult } from "../effect-result.js";
import type { ActionLog, JsonValue } from "../rng.js";
import { consumeIfMatches } from "../services/immunity-service.js";
import { applyModifier } from "../services/modifier-math.js";
import {
  evaluateBool,
  evaluateCount,
  isCountPredicate,
  isGatingPredicate,
} from "../services/predicate-eval.js";
import { consumeCharge, findMatching } from "../services/trigger-service.js";
import { resolve as registryResolve } from "./effect-kind-resolver-registry.js";
import type { PipelineServices } from "./pipeline-services.js";

// ---------------------------------------------------------------------------
// MAX_TRIGGER_DEPTH — recursion guard for trigger-fired triggers
//
// WEB.4.E: If a trigger response fires another trigger (e.g. Counterattack's
// steal-back fires AfterStealEnergyTargetingMe on the original attacker who
// also holds a Counterattack), we could infinite-loop. Cap at depth 1.
// triggerDepth=0 → normal play → dispatch triggers as usual.
// triggerDepth=1 → inside a trigger response → dispatch is skipped.
// ---------------------------------------------------------------------------

const MAX_TRIGGER_DEPTH = 1;

// ---------------------------------------------------------------------------
// resolveTriggerEvent — maps a CardEffect + ctx to a TriggerEvent (if any)
//
// Determines whether this effect.kind + ctx produces a TriggerEvent that
// passive trigger holders should react to.
//
// Returns:
//   { event, ownerSeat } if this effect fires a trigger event.
//   null if this effect kind doesn't fire any trigger.
//
// Fire-point semantics:
//   StealEnergy:    victimSeat fires AfterStealEnergyTargetingMe
//                   (the victim whose energy was stolen retaliates).
//   Destroy:        the facility owner fires AfterFacilityDestroyedMine.
//                   Owner = ctx.victimSeat when target=OpponentChosen/etc.;
//                   = ctx.sourceSeat when target=Self/Drawer.
//                   Use ctx.victimSeat if set, else ctx.sourceSeat.
//   Decommission:   facility owner fires AfterFacilityDecommissionedMine.
//                   Forced decommission (OpponentChosen) → ctx.victimSeat.
//                   Voluntary (Self/Drawer) → ctx.sourceSeat.
//   Trigger:        not a fire-point itself (registration only).
//   Other kinds:    no trigger event.
// ---------------------------------------------------------------------------

function resolveTriggerEvent(
  effect: CardEffect,
  ctx: Readonly<EffectContext>,
): { event: TriggerEvent; ownerSeat: number } | null {
  switch (effect.kind) {
    case "StealEnergy": {
      // victimSeat = the player whose energy was stolen → they retaliate.
      if (ctx.victimSeat >= 0 && ctx.victimSeat !== ctx.sourceSeat) {
        return { event: "AfterStealEnergyTargetingMe", ownerSeat: ctx.victimSeat };
      }
      return null;
    }
    case "Destroy": {
      // The facility owner fires (opponent when targeted by opponent, or self when self-destroying).
      // victimSeat is set to the target owner by the caller when targeting an opponent.
      const ownerSeat = ctx.victimSeat >= 0 ? ctx.victimSeat : ctx.sourceSeat;
      return { event: "AfterFacilityDestroyedMine", ownerSeat };
    }
    case "Decommission": {
      // Forced decommission (opponent card) → victimSeat owns the facility.
      // Voluntary (self) → sourceSeat owns the facility.
      const ownerSeat =
        ctx.victimSeat >= 0 && ctx.victimSeat !== ctx.sourceSeat ? ctx.victimSeat : ctx.sourceSeat;
      return { event: "AfterFacilityDecommissionedMine", ownerSeat };
    }
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// dispatchTriggers — fire matching passive triggers for an event
//
// Called after Applied resolver result + afterApplyHook (step 7).
// Skipped when:
//   • services.trigger is undefined (no trigger state wired).
//   • ctx.triggerDepth >= MAX_TRIGGER_DEPTH (recursion guard).
//   • No matching triggers found.
//
// For each matching trigger:
//   1. consumeCharge (decrement / discard when exhausted).
//   2. Run each response effect via runSingleEffect at depth+1.
//   3. Append ActionLog entry: trigger.fired { ... }.
//
// Response effect ctx:
//   sourceSeat = trigger.ownerSeat    (owner retaliates)
//   victimSeat = originalSourceSeat   (original attacker becomes victim)
//   triggerDepth = (ctx.triggerDepth ?? 0) + 1
//   Other ctx fields preserved (hooks, etc.) — but chosenBranchId cleared
//   (trigger responses are unconditional effects, not player-chosen).
// ---------------------------------------------------------------------------

function dispatchTriggers(
  effectKindOfTrigger: CardEffect,
  originalCtx: Readonly<EffectContext>,
  originalSourceSeat: number,
  services: PipelineServices,
  log: ActionLog,
): { services: PipelineServices; log: ActionLog } {
  const currentDepth = originalCtx.triggerDepth ?? 0;

  // Recursion guard.
  if (currentDepth >= MAX_TRIGGER_DEPTH) {
    return { services, log };
  }

  // No trigger state wired → skip.
  if (services.trigger === undefined) {
    return { services, log };
  }

  // Determine event from effect kind + ctx.
  const eventResult = resolveTriggerEvent(effectKindOfTrigger, originalCtx);
  if (eventResult === null) {
    return { services, log };
  }

  const { event, ownerSeat } = eventResult;
  const matching = findMatching(services.trigger, event, ownerSeat);

  if (matching.length === 0) {
    return { services, log };
  }

  let currentServices = services;
  let currentLog = log;

  for (const trigger of matching) {
    // 1. Consume one charge.
    const triggerState = currentServices.trigger;
    if (triggerState === undefined) {
      break;
    }
    const chargeResult = consumeCharge(triggerState, currentLog, trigger.instanceId);
    currentLog = chargeResult.log;
    currentServices = { ...currentServices, trigger: chargeResult.newState };

    // 2. Build response context: owner retaliates against original attacker.
    // Omit chosenBranchId entirely (exactOptionalPropertyTypes=true: assigning
    // undefined is not valid for an optional property). Trigger response effects
    // are unconditional — no branch selection needed.
    const { chosenBranchId: _dropBranch, ...restCtx } = originalCtx;
    const responseCtx: EffectContext = {
      ...restCtx,
      sourceSeat: ownerSeat,
      victimSeat: originalSourceSeat,
      triggerDepth: currentDepth + 1,
    };

    // 3. Run each response effect.
    let responseFired = 0;
    for (const responseEffect of trigger.response) {
      const effectResult = runSingleEffect(
        null, // no card def for trigger response
        responseEffect,
        responseCtx,
        currentServices,
        currentLog,
      );
      currentLog = effectResult.log;
      currentServices = effectResult.services;
      if (effectResult.result === EffectResult.Applied) {
        responseFired++;
      }
    }

    // 4. ActionLog: trigger.fired entry.
    currentLog = currentLog.append({
      kind: "trigger.fired",
      data: {
        triggerInstanceId: trigger.instanceId,
        ownerSeat,
        originalSourceSeat,
        event,
        responseEffectCount: trigger.response.length,
        responseFired,
        triggerDepth: currentDepth + 1,
        exhausted: chargeResult.exhausted,
      } as JsonValue,
    });
  }

  return { services: currentServices, log: currentLog };
}

// ---------------------------------------------------------------------------
// PipelineEffectResult — per-effect outcome
// ---------------------------------------------------------------------------

export interface PipelineEffectResult {
  readonly result: EffectResult;
  readonly log: ActionLog;
  readonly services: PipelineServices;
}

// ---------------------------------------------------------------------------
// PipelineCardResult — whole-card outcome
// ---------------------------------------------------------------------------

export interface PipelineCardResult {
  readonly result: EffectResult;
  readonly log: ActionLog;
  readonly services: PipelineServices;
}

// ---------------------------------------------------------------------------
// scaleByPredicate — port of ScaleByPredicate (private in registry, lifted here)
// ---------------------------------------------------------------------------

function scaleByPredicate(
  effect: CardEffect,
  ctx: Readonly<EffectContext>,
  services: PipelineServices,
): number {
  const baseVal = effect.valueInt ?? 0;
  const pred = effect.predicate ?? "";

  if (isCountPredicate(pred)) {
    const count = evaluateCount(ctx.sourceSeat, pred, services.countProvider);
    const scaled = (effect.valueInt ?? 0) * count;
    // Apply ModifierMath only when mult or add are set.
    const mult = effect.mult ?? 0;
    const add = effect.add ?? 0;
    if (mult !== 0 && Math.abs(mult - 1) > 0.0001) {
      return applyModifier(scaled, mult, add, "EffectResolver").result;
    }
    if (add !== 0) {
      return applyModifier(scaled, 1, add, "EffectResolver").result;
    }
    return scaled;
  }

  // No count predicate — use base value directly.
  const mult = effect.mult ?? 0;
  const add = effect.add ?? 0;
  if (mult !== 0 && Math.abs(mult - 1) > 0.0001) {
    return applyModifier(baseVal, mult, add, "EffectResolver").result;
  }
  if (add !== 0) {
    return applyModifier(baseVal, 1, add, "EffectResolver").result;
  }
  return baseVal;
}

// ---------------------------------------------------------------------------
// runSingleEffect — port of EffectPipeline.Run(card, effect, ctx)
// ---------------------------------------------------------------------------

export function runSingleEffect(
  card: CardDef | null,
  effect: CardEffect,
  ctx: Readonly<EffectContext>,
  services: PipelineServices,
  log: ActionLog,
): PipelineEffectResult {
  const cardId = card !== null ? card.name : "<null>";
  const kindStr = effect.kind;

  try {
    // beforeApplyHook
    ctx.beforeApplyHook?.(ctx);

    // WEB.4.B — Immunity check.
    // Runs BEFORE onInterceptHook and predicate gate so that immunity shields
    // even conditional (predicate-gated) effects.  The defender's seat is the
    // victimSeat when the effect targets an opponent, or sourceSeat when it
    // targets the drawer (Self/Drawer events).
    //
    // Per-tile check: pass ctx.targetTileIndices[0] when it is defined so that
    // single-tile immunity (Enhanced Measures / Infrastructure Bailout) is only
    // consumed when the specific tile is targeted.
    // Codex WEB.4.B R2 HIGH fix: skip pipeline-level immunity for Global + affectsAll
    // multi-tile effects. Pipeline check is single-seat; multi-tile resolvers consult
    // immunity per-tile (per-owner) inside their loop. If pipeline ran here, a stale
    // or caller-supplied victimSeat could intercept the entire global sweep before
    // per-tile logic runs (false positive blocking other owners' tiles).
    const isGlobalMultiTile = effect.target === "Global" && effect.affectsAll === true;
    const immunitySeat = isGlobalMultiTile
      ? -1 // sentinel — skip pipeline-level check; per-tile handles it
      : effect.target === "Self" || effect.target === "Drawer"
        ? ctx.sourceSeat
        : ctx.victimSeat;
    if (immunitySeat >= 0) {
      const tileForCheck = ctx.targetTileIndices[0]; // may be undefined (seat-level)
      const consumeResult = consumeIfMatches(
        services.immunity,
        log,
        immunitySeat,
        effect.kind,
        tileForCheck,
      );
      if (consumeResult.intercepted) {
        const nextLog = consumeResult.log.append({
          kind: "pipeline.intercepted-by-immunity",
          data: {
            cardId,
            effectKind: kindStr,
            ownerSeat: immunitySeat,
            tileIndex: tileForCheck ?? null,
          } as JsonValue,
        });
        return {
          result: EffectResult.Intercepted,
          log: nextLog,
          services: { ...services, immunity: consumeResult.newState },
        };
      }
    }

    // onInterceptHook
    if (ctx.onInterceptHook?.(ctx)) {
      const nextLog = log.append({
        kind: "pipeline.intercepted",
        data: { cardId, effectKind: kindStr, sourceSeat: ctx.sourceSeat } as JsonValue,
      });
      return { result: EffectResult.Intercepted, log: nextLog, services };
    }

    // Predicate gate
    const pred = effect.predicate ?? "";
    if (isGatingPredicate(pred)) {
      let passed: boolean;
      try {
        passed = evaluateBool(ctx.sourceSeat, pred, services.countProvider);
      } catch (ex) {
        console.error(`[EffectPipeline] predicate '${pred}' threw: ${String(ex)}`);
        const nextLog = log.append({
          kind: "pipeline.failed",
          data: { cardId, effectKind: kindStr, reason: "predicate-threw" } as JsonValue,
        });
        return { result: EffectResult.Failed, log: nextLog, services };
      }
      if (!passed) {
        const nextLog = log.append({
          kind: "pipeline.skipped",
          data: { cardId, effectKind: kindStr, reason: "predicate-gate-not-met" } as JsonValue,
        });
        return { result: EffectResult.Skipped, log: nextLog, services };
      }
    }

    // BuildCostMod-on-Whenever guard: deferred-to-build path.
    // Whenever-discard cards' BuildCostMod effects never resolve at draw —
    // they are consumed at build time by BuildCostService hand-scan path.
    if (card !== null && effect.kind === "BuildCostMod" && card.discardMode === "Whenever") {
      const nextLog = log.append({
        kind: "pipeline.skipped",
        data: { cardId, effectKind: kindStr, reason: "DeferredToBuild" } as JsonValue,
      });
      return { result: EffectResult.Skipped, log: nextLog, services };
    }

    // Resolver dispatch
    const scaledDelta = scaleByPredicate(effect, ctx, services);
    const resolveResult = registryResolve(effect, ctx, services, log, scaledDelta);

    if (resolveResult === null) {
      // No resolver registered (unknown kind, not a deferred stub path)
      const nextLog = log.append({
        kind: "pipeline.skipped",
        data: { cardId, effectKind: kindStr, reason: "no-resolver" } as JsonValue,
      });
      return { result: EffectResult.Skipped, log: nextLog, services };
    }

    const { result, log: resolveLog, services: resolveServices } = resolveResult;

    // Unity parity (EffectPipeline.cs:80): afterApplyHook fires after ANY
    // registered resolver result, not only Applied. StealEnergy victim-empty
    // (Skipped) and similar paths still trigger the hook.
    ctx.afterApplyHook?.(ctx);

    // WEB.4.E — Trigger dispatch (step 7).
    // Fires ONLY when resolver returned Applied. afterApplyHook already ran above.
    // Recursion guard: skip when triggerDepth >= MAX_TRIGGER_DEPTH.
    // originalSourceSeat = ctx.sourceSeat at the time of the primary effect (the
    // attacker who triggered the reaction; response ctx swaps src/victim).
    let postTriggerLog = resolveLog;
    let postTriggerServices = resolveServices;
    if (result === EffectResult.Applied) {
      const triggerDispatch = dispatchTriggers(
        effect,
        ctx,
        ctx.sourceSeat, // originalSourceSeat
        postTriggerServices,
        postTriggerLog,
      );
      postTriggerLog = triggerDispatch.log;
      postTriggerServices = triggerDispatch.services;
    }

    const nextLog = postTriggerLog.append({
      kind: "pipeline.result",
      data: { cardId, effectKind: kindStr, result, sourceSeat: ctx.sourceSeat } as JsonValue,
    });

    return { result, log: nextLog, services: postTriggerServices };
  } catch (e) {
    console.error(`[EffectPipeline] resolver threw: ${String(e)}`);
    const nextLog = log.append({
      kind: "pipeline.failed",
      data: { cardId, effectKind: kindStr, reason: "resolver-threw" } as JsonValue,
    });
    return { result: EffectResult.Failed, log: nextLog, services };
  }
}

// ---------------------------------------------------------------------------
// runCard — port of EffectPipeline.Run(card, ctx)
//
// Aggregation logic (mirrors Unity):
//   • any Failed        → Failed
//   • any Intercepted (no Failed) → Intercepted
//   • all Skipped       → Skipped
//   • ≥1 Applied        → Applied
// ---------------------------------------------------------------------------

export function runCard(
  card: CardDef | null,
  ctx: Readonly<EffectContext>,
  services: PipelineServices,
  log: ActionLog,
): PipelineCardResult {
  if (card === null || card.effects.length === 0) {
    return { result: EffectResult.Skipped, log, services };
  }

  // Auto-populate cardTags if not set on ctx — done via service context in pure approach.
  // (Unity mutates ctx.cardTags here; we leave ctx immutable and rely on caller.)

  // ---------------------------------------------------------------------------
  // WEB.3.D.2 — OR-choice player-choice model.
  //
  // Detect whether this card has any branched effects (branchId set on ≥1 effect).
  // If yes:
  //   • ctx.chosenBranchId === undefined → Skipped (fail-closed; player must choose).
  //   • ctx.chosenBranchId is set → run effects matching branchId + all no-branchId effects.
  //     Effects with a non-matching branchId are "branch-skipped" (not Applied/Skipped/Failed).
  // If no effect has branchId → all effects run unconditionally (legacy path, no regression).
  //
  // ActionLog: appends "pipeline.branch-chosen" entry when a choice was resolved.
  // ---------------------------------------------------------------------------
  const hasBranches = card.effects.some((e) => e.branchId !== undefined);

  if (hasBranches) {
    const cardId = card.name;
    if (ctx.chosenBranchId === undefined) {
      // Fail-closed: player has not chosen a branch yet.
      const nextLog = log.append({
        kind: "pipeline.branch-required",
        data: {
          cardId,
          branches: [
            ...new Set(card.effects.map((e) => e.branchId).filter((b) => b !== undefined)),
          ],
          reason: "chosenBranchId-not-set",
        } as JsonValue,
      });
      return { result: EffectResult.Skipped, log: nextLog, services };
    }

    // Player has chosen a branch — execute only matching + no-branchId effects.
    const chosenBranchId = ctx.chosenBranchId;
    const executedBranchIds: string[] = [];
    const skippedBranchIds: string[] = [];

    let applied = 0;
    let _skipped = 0;
    let failed = 0;
    let intercepted = 0;
    let currentLog = log;
    let currentServices = services;

    for (const eff of card.effects) {
      if (eff.branchId !== undefined && eff.branchId !== chosenBranchId) {
        // Branch not chosen — do NOT run, do NOT count as Skipped.
        skippedBranchIds.push(eff.branchId);
        continue;
      }
      if (eff.branchId !== undefined) {
        executedBranchIds.push(eff.branchId);
      }

      const effResult = runSingleEffect(card, eff, ctx, currentServices, currentLog);
      currentLog = effResult.log;
      currentServices = effResult.services;

      switch (effResult.result) {
        case EffectResult.Applied:
          applied++;
          break;
        case EffectResult.Skipped:
          _skipped++;
          break;
        case EffectResult.Failed:
          failed++;
          break;
        case EffectResult.Intercepted:
          intercepted++;
          break;
      }
    }

    // ActionLog: branch-chosen entry for replay clarity.
    const branchLog = currentLog.append({
      kind: "pipeline.branch-chosen",
      data: {
        cardId,
        chosenBranchId,
        executedEffects: executedBranchIds,
        skippedEffects: [...new Set(skippedBranchIds)],
      } as JsonValue,
    });

    let finalResult: EffectResult;
    if (failed > 0) {
      finalResult = EffectResult.Failed;
    } else if (intercepted > 0 && applied === 0) {
      finalResult = EffectResult.Intercepted;
    } else if (applied > 0) {
      finalResult = EffectResult.Applied;
    } else {
      finalResult = EffectResult.Skipped;
    }

    return { result: finalResult, log: branchLog, services: currentServices };
  }

  // ---------------------------------------------------------------------------
  // Legacy path: no branched effects — all effects run unconditionally.
  // ---------------------------------------------------------------------------

  let applied = 0;
  let _skipped = 0;
  let failed = 0;
  let intercepted = 0;

  let currentLog = log;
  let currentServices = services;

  for (const eff of card.effects) {
    const effResult = runSingleEffect(card, eff, ctx, currentServices, currentLog);
    currentLog = effResult.log;
    currentServices = effResult.services;

    switch (effResult.result) {
      case EffectResult.Applied:
        applied++;
        break;
      case EffectResult.Skipped:
        _skipped++;
        break;
      case EffectResult.Failed:
        failed++;
        break;
      case EffectResult.Intercepted:
        intercepted++;
        break;
    }
  }

  let finalResult: EffectResult;
  if (failed > 0) {
    finalResult = EffectResult.Failed;
  } else if (intercepted > 0 && applied === 0) {
    finalResult = EffectResult.Intercepted;
  } else if (applied > 0) {
    finalResult = EffectResult.Applied;
  } else {
    finalResult = EffectResult.Skipped;
  }

  return { result: finalResult, log: currentLog, services: currentServices };
}
